import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { QueryClientProvider } from '@tanstack/react-query';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { store } from './store';
import { useAppSelector } from './store';
import { queryClient } from './lib/queryClient';
import { RBACProvider } from './contexts/RBACContext';
import { NotificationProvider, useNotifications } from './contexts/NotificationContext';
import AuthInitializer from './components/auth/AuthInitializer';
import { ToastContainer } from './components/ui/Toast';
import Layout from './components/layout/Layout';
import { PROJECT_LIST_PERMISSIONS, RETAINER_LIST_PERMISSIONS } from './components/layout/navigation';
import { ProtectedRoute as RBACProtectedRoute } from './components/rbac/ProtectedRoute';
import {
  withLazyLoading,
  withNamedExportLazyLoading,
  preloadComponentSequence
} from './utils/lazyLoading';
import { initPerformanceMonitoring } from './utils/webVitals';
import { registerServiceWorker } from './utils/serviceWorker';
import { initializeAssetOptimization } from './utils/assetOptimization';
import LazyLoadingMonitor from './components/performance/LazyLoadingMonitor';
import AssetLoadingMonitor from './components/performance/AssetLoadingMonitor';

// Critical pages loaded immediately
import Login from './pages/Login';
import GoogleCallback from './pages/GoogleCallback';
import NotFound from './pages/NotFound';
import Unauthorized from './pages/Unauthorized';

// Lazy-loaded pages for better performance
const Dashboard = withLazyLoading(
  () => import('./pages/Dashboard'),
  'page'
);

const Pods = withLazyLoading(
  () => import('./pages/Pods'),
  'page'
);

const PodAssignments = withLazyLoading(
  () => import('./pages/PodAssignments'),
  'page'
);

const PodUtilization = withLazyLoading(
  () => import('./pages/PodUtilization'),
  'page'
);

const PodAnalytics = withLazyLoading(
  () => import('./pages/PodAnalytics'),
  'page'
);

const PodLeadDashboard = withLazyLoading(
  () => import('./pages/PodLeadDashboard'),
  'page'
);

const PodAssignmentDashboard = withLazyLoading(
  () => import('./pages/PodAssignmentDashboard'),
  'page'
);

const AssignmentTimeTrackingDemo = withLazyLoading(
  () => import('./pages/AssignmentTimeTrackingDemo'),
  'page'
);

const Projects = withNamedExportLazyLoading(
  () => import('./pages/Projects'),
  'Projects',
  'page'
);

const ProjectDetailPage = withNamedExportLazyLoading(
  () => import('./pages/ProjectDetail'),
  'ProjectDetailPage',
  'page'
);

const InternalProjectDetailPage = withNamedExportLazyLoading(
  () => import('./pages/InternalProjectDetail'),
  'InternalProjectDetailPage',
  'page'
);

const Retainers = withNamedExportLazyLoading(
  () => import('./pages/Retainers'),
  'Retainers',
  'page'
);

const Internal = withNamedExportLazyLoading(
  () => import('./pages/Internal'),
  'Internal',
  'page'
);

const Analytics = withLazyLoading(
  () => import('./pages/Analytics'),
  'page'
);

const PerformancePage = withLazyLoading(
  () => import('./pages/PerformancePage'),
  'page'
);

const RetrospectiveAnalysis = withLazyLoading(
  () => import('./pages/RetrospectiveAnalysis'),
  'page'
);

const ReportingDashboard = withLazyLoading(
  () => import('./pages/ReportingDashboard'),
  'page'
);

const EpicReporting = withLazyLoading(
  () => import('./pages/EpicReporting'),
  'page'
);

const ProjectEpicAnalysisPage = withLazyLoading(
  () => import('./pages/ProjectEpicAnalysisPage'),
  'page'
);

const SprintReporting = withLazyLoading(
  () => import('./pages/SprintReporting'),
  'page'
);

const Forecasts = withLazyLoading(
  () => import('./pages/Forecasts'),
  'page'
);

const Settings = withLazyLoading(
  () => import('./pages/Settings'),
  'page'
);

const QueueMonitor = withLazyLoading(
  () => import('./pages/QueueMonitor'),
  'page'
);

const SystemAdmin = withLazyLoading(
  () => import('./pages/SystemAdmin'),
  'page'
);

const ImportMonitor = withLazyLoading(
  () => import('./pages/ImportMonitor'),
  'page'
);

const BreatheHRPage = withLazyLoading(
  () => import('./pages/BreatheHR/BreatheHRPage'),
  'page'
);

const SsrMonitoring = withLazyLoading(
  () => import('./pages/SsrMonitoring'),
  'page'
);

const Incidents = withLazyLoading(
  () => import('./pages/Incidents'),
  'page'
);

const StylesCheckPage = withLazyLoading(
  () => import('./pages/Tools/StylesCheckPage'),
  'page'
);

const IncidentDetail = withLazyLoading(
  () => import('./pages/IncidentDetail'),
  'page'
);

const Worktime = withLazyLoading(
  () => import('./pages/Worktime'),
  'page'
);

const Surveys = withLazyLoading(
  () => import('./pages/Surveys'),
  'page'
);

const SurveyResults = withLazyLoading(
  () => import('./pages/SurveyResults'),
  'page'
);

const PresenceSettings = withLazyLoading(
  () => import('./pages/PresenceSettings'),
  'page'
);

const EpicAnalysis = withLazyLoading(
  () => import('./pages/EpicAnalysis'),
  'page'
);

const ClickUpManagement = withLazyLoading(
  () => import('./pages/ClickUpManagement'),
  'page'
);

const ClickUpTasks = withLazyLoading(
  () => import('./pages/ClickUpTasks'),
  'page'
);

const QuoteManagement = withLazyLoading(
  () => import('./pages/QuoteManagement'),
  'page'
);

const TimeTracking = withLazyLoading(
  () => import('./pages/TimeTracking'),
  'page'
);

const TeamCalendarPage = withLazyLoading(
  () => import('./pages/TeamCalendarPage'),
  'page'
);

const AbsenceOverview = withLazyLoading(
  () => import('./pages/AbsenceOverview'),
  'page'
);

const UserProfile = withLazyLoading(
  () => import('./pages/UserProfile'),
  'page'
);

const AuditDashboard = withLazyLoading(
  () => import('./pages/AuditDashboard'),
  'page'
);

const RetentionPolicyDashboard = withLazyLoading(
  () => import('./pages/RetentionPolicyDashboard'),
  'page'
);

const AdminTools = withLazyLoading(
  () => import('./pages/AdminTools'),
  'page'
);

const Users = withLazyLoading(
  () => import('./pages/Users'),
  'page'
);

const Employees = withLazyLoading(
  () => import('./pages/Employees'),
  'page'
);

const EmployeeDetailPage = withLazyLoading(
  () => import('./pages/Employees/EmployeeDetailPage'),
  'page'
);

const AssignmentTimeline = withLazyLoading(
  () => import('./pages/AssignmentTimeline'),
  'page'
);

const AssignmentManagement = withLazyLoading(
  () => import('./pages/AssignmentManagement'),
  'page'
);

const TotpCodes = withLazyLoading(
  () => import('./pages/TotpCodes'),
  'page'
);

const HolidayDashboard = withNamedExportLazyLoading(
  () => import('./pages/Holidays'),
  'HolidayDashboard',
  'page'
);

const HolidayApprovals = withNamedExportLazyLoading(
  () => import('./pages/Holidays'),
  'HolidayApprovals',
  'page'
);

const HolidayAllowanceAdmin = withNamedExportLazyLoading(
  () => import('./pages/Holidays'),
  'HolidayAllowanceAdmin',
  'page'
);

const AllowanceDetailPage = withNamedExportLazyLoading(
  () => import('./pages/Holidays'),
  'AllowanceDetailPage',
  'page'
);

const BulkAllowanceOperations = withNamedExportLazyLoading(
  () => import('./pages/Holidays'),
  'BulkAllowanceOperations',
  'page'
);

// HR Pages
const SicknessPage = withNamedExportLazyLoading(
  () => import('./pages/Sickness'),
  'SicknessPage',
  'page'
);

const TrainingPage = withNamedExportLazyLoading(
  () => import('./pages/Training'),
  'TrainingPage',
  'page'
);

const ExpensesPage = withNamedExportLazyLoading(
  () => import('./pages/Expenses'),
  'ExpensesPage',
  'page'
);

const BenefitsPage = withNamedExportLazyLoading(
  () => import('./pages/Benefits'),
  'BenefitsPage',
  'page'
);

// Admin Pages for HR Data
const WorkingPatternsPage = withLazyLoading(
  () => import('./pages/Admin/WorkingPatternsPage'),
  'page'
);

const DepartmentsPage = withLazyLoading(
  () => import('./pages/Admin/DepartmentsPage'),
  'page'
);

const DivisionsPage = withLazyLoading(
  () => import('./pages/Admin/DivisionsPage'),
  'page'
);

const LocationsPage = withLazyLoading(
  () => import('./pages/Admin/LocationsPage'),
  'page'
);

const RoleManagementPage = withLazyLoading(
  () => import('./pages/Admin/RoleManagementPage'),
  'page'
);

const PermissionUsageReportPage = withLazyLoading(
  () => import('./pages/Admin/PermissionUsageReportPage'),
  'page'
);

const InfrastructureDiscoveryPage = withLazyLoading(
  () => import('./pages/Admin/InfrastructureDiscoveryPage'),
  'page'
);

// Initialize performance monitoring, service worker, and preload critical pages
if (typeof window !== 'undefined') {
  // Initialize performance monitoring
  initPerformanceMonitoring();

  // Initialize asset optimization
  initializeAssetOptimization();

  // Register service worker for caching and offline support
  registerServiceWorker({
    onSuccess: () => console.log('✅ App is cached for offline use'),
    onUpdate: () => console.log('🔄 New content available, please refresh'),
    onOfflineReady: () => console.log('📱 App is ready to work offline'),
    onNeedRefresh: () => {
      // You could show a toast notification here
      console.log('🔄 Please refresh to get the latest version');
    }
  });

  // Enhanced preloading strategy
  preloadComponentSequence([
    // High priority - Dashboard (most visited page)
    {
      importFn: () => import('./pages/Dashboard'),
      priority: 'high',
      delay: 500
    },
    // Medium priority - Common pages
    {
      importFn: () => import('./pages/Projects'),
      priority: 'medium',
      delay: 1000
    },
    {
      importFn: () => import('./pages/PodUtilization'),
      priority: 'medium',
      delay: 1500
    },
    {
      importFn: () => import('./pages/TimeTracking'),
      priority: 'medium',
      delay: 1750
    },
    // Low priority - Less common pages
    {
      importFn: () => import('./pages/Analytics'),
      priority: 'low',
      delay: 2000
    },
    {
      importFn: () => import('./pages/Settings'),
      priority: 'low',
      delay: 3000
    }
  ]);

  // Preload chart components after pages
  setTimeout(() => {
    import('./components/charts/LazyChartComponents').then(module => {
      module.preloadChartComponents();
    });
  }, 2000);
}

// Private Route Component (for basic authentication)
const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

// Route-based preloading hook
const useRoutePreloading = () => {
  React.useEffect(() => {
    const handleRouteChange = () => {
      const currentPath = window.location.pathname;

      // Preload components based on current route
      import('./components/charts/LazyChartComponents').then(module => {
        module.preloadComponentsForRoute(currentPath);
      });

      // Preload admin components if on admin routes
      if (currentPath.startsWith('/admin')) {
        import('./components/admin/LazyAdminComponents').then(module => {
          const section = currentPath.split('/').pop() || 'overview';
          module.preloadAdminSection(section);
        });
      }
    };

    // Initial preload
    handleRouteChange();

    // Listen for route changes
    window.addEventListener('popstate', handleRouteChange);

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, []);
};

// App Routes Component
const AppRoutes: React.FC = () => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  // Enable route-based preloading
  useRoutePreloading();

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <Login />}
      />
      <Route path="/auth/google/callback" element={<GoogleCallback />} />
      <Route path="/unauthorized" element={<Unauthorized />} />
      <Route
        path="/*"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        <Route index element={
          <RBACProtectedRoute permissions={[{ resource: 'dashboard', action: 'view' }]}>
            <Dashboard />
          </RBACProtectedRoute>
        } />
        <Route path="pods" element={
          <RBACProtectedRoute
            permissions={[{ resource: 'pods', action: 'view' }]}
          >
            <Pods />
          </RBACProtectedRoute>
        } />
        <Route path="pods/:podId/analytics" element={
          <RBACProtectedRoute
            permissions={[{ resource: 'pods', action: 'view' }]}
          >
            <PodAnalytics />
          </RBACProtectedRoute>
        } />
        <Route path="pod-dashboard" element={
          <RBACProtectedRoute
            permissions={[{ resource: 'pods', action: 'view' }]}
          >
            <PodLeadDashboard />
          </RBACProtectedRoute>
        } />
        {/* Legacy route for backwards compatibility */}
        <Route path="pod-lead-dashboard" element={
          <RBACProtectedRoute
            permissions={[{ resource: 'pods', action: 'view' }]}
          >
            <PodLeadDashboard />
          </RBACProtectedRoute>
        } />
        <Route path="pod-assignments" element={
          <RBACProtectedRoute permissions={[{ resource: 'pod_assignments', action: 'view' }]}>
            <PodAssignments />
          </RBACProtectedRoute>
        } />
        <Route path="pod-assignment-dashboard" element={
          <RBACProtectedRoute permissions={[{ resource: 'project_assignments', action: 'view' }]}>
            <PodAssignmentDashboard />
          </RBACProtectedRoute>
        } />
        <Route path="assignment-time-tracking-demo" element={
          <RBACProtectedRoute permissions={[{ resource: 'time_entries', action: 'view' }]}>
            <AssignmentTimeTrackingDemo />
          </RBACProtectedRoute>
        } />
        <Route path="assignment-timeline" element={
          <RBACProtectedRoute permissions={[{ resource: 'project_assignments', action: 'view' }]}>
            <AssignmentTimeline />
          </RBACProtectedRoute>
        } />
<Route path="assignment-management" element={
          <RBACProtectedRoute permissions={[{ resource: 'project_assignments', action: 'manage' }]}>
            <AssignmentManagement />
          </RBACProtectedRoute>
        } />
        <Route path="pod-utilization" element={
          <RBACProtectedRoute permissions={[{ resource: 'pods', action: 'view', scope: 'global' }]}>
            <PodUtilization />
          </RBACProtectedRoute>
        } />
        <Route path="builds" element={
          <RBACProtectedRoute permissions={PROJECT_LIST_PERMISSIONS}>
            <Projects />
          </RBACProtectedRoute>
        } />
        <Route path="projects/new" element={
          <RBACProtectedRoute permissions={[{ resource: 'projects', action: 'create' }]}>
            <Projects />
          </RBACProtectedRoute>
        } />
        <Route path="projects/:id" element={
          <RBACProtectedRoute permissions={[{ resource: 'projects', action: 'view' }]}>
            <ProjectDetailPage />
          </RBACProtectedRoute>
        } />
        <Route path="projects/:id/edit" element={
          <RBACProtectedRoute permissions={[{ resource: 'projects', action: 'edit' }]}>
            <ProjectDetailPage />
          </RBACProtectedRoute>
        } />
        <Route path="projects/:id/epic-analysis" element={
          <RBACProtectedRoute permissions={[{ resource: 'projects', action: 'view' }]}>
            <ProjectEpicAnalysisPage />
          </RBACProtectedRoute>
        } />
        {/* Redirect old projects route to builds */}
        <Route path="projects" element={<Navigate to="/builds" replace />} />
        <Route path="retainers" element={
          <RBACProtectedRoute permissions={RETAINER_LIST_PERMISSIONS}>
            <Retainers />
          </RBACProtectedRoute>
        } />
        <Route path="internal" element={
          <RBACProtectedRoute permissions={PROJECT_LIST_PERMISSIONS}>
            <Internal />
          </RBACProtectedRoute>
        } />
        <Route path="internal/:id" element={
          <RBACProtectedRoute permissions={[{ resource: 'projects', action: 'view' }]}>
            <InternalProjectDetailPage />
          </RBACProtectedRoute>
        } />
        <Route path="internal/:id/edit" element={
          <RBACProtectedRoute permissions={[{ resource: 'projects', action: 'edit' }]}>
            <InternalProjectDetailPage />
          </RBACProtectedRoute>
        } />
        <Route path="forecasts" element={
          <RBACProtectedRoute permissions={[{ resource: 'forecasts', action: 'view' }]}>
            <Forecasts />
          </RBACProtectedRoute>
        } />
        <Route path="time-tracking" element={
          <RBACProtectedRoute
            permissions={[{ resource: 'time_entries', action: 'view', scope: 'self' }]}
          >
            <TimeTracking />
          </RBACProtectedRoute>
        } />
        <Route path="team-calendar" element={
          <RBACProtectedRoute permissions={[{ resource: 'absences', action: 'view' }]}>
            <TeamCalendarPage />
          </RBACProtectedRoute>
        } />
        <Route path="holidays" element={
          <RBACProtectedRoute permissions={[{ resource: 'holidays', action: 'view', scope: 'self' }]}>
            <HolidayDashboard />
          </RBACProtectedRoute>
        } />
        <Route path="holidays/approvals" element={
          <RBACProtectedRoute permissions={[{ resource: 'holidays', action: 'approve' }]}>
            <HolidayApprovals />
          </RBACProtectedRoute>
        } />
        <Route path="holidays/admin/allowances" element={
          <RBACProtectedRoute permissions={[{ resource: 'holiday_allowances', action: 'view', scope: 'global' }]}>
            <HolidayAllowanceAdmin />
          </RBACProtectedRoute>
        } />
        <Route path="holidays/admin/allowances/:id" element={
          <RBACProtectedRoute permissions={[{ resource: 'holiday_allowances', action: 'view', scope: 'global' }]}>
            <AllowanceDetailPage />
          </RBACProtectedRoute>
        } />
        <Route path="holidays/admin/bulk-operations" element={
          <RBACProtectedRoute permissions={[{ resource: 'holiday_allowances', action: 'edit', scope: 'global' }]}>
            <BulkAllowanceOperations />
          </RBACProtectedRoute>
        } />
        {/* HR Pages */}
        <Route path="absence-overview" element={
          <RBACProtectedRoute permissions={[{ resource: 'absences', action: 'view', scope: 'global' }]}>
            <AbsenceOverview />
          </RBACProtectedRoute>
        } />
        <Route path="sickness" element={
          <RBACProtectedRoute permissions={[{ resource: 'sickness', action: 'view', scope: 'self' }]}>
            <SicknessPage />
          </RBACProtectedRoute>
        } />
        <Route path="training" element={
          <RBACProtectedRoute permissions={[{ resource: 'training', action: 'view', scope: 'self' }]}>
            <TrainingPage />
          </RBACProtectedRoute>
        } />
        <Route path="expenses" element={
          <RBACProtectedRoute permissions={[{ resource: 'expenses', action: 'view', scope: 'self' }]}>
            <ExpensesPage />
          </RBACProtectedRoute>
        } />
        <Route path="benefits" element={
          <RBACProtectedRoute permissions={[{ resource: 'benefits', action: 'view', scope: 'self' }]}>
            <BenefitsPage />
          </RBACProtectedRoute>
        } />
        <Route path="user/:id" element={
          <RBACProtectedRoute permissions={[{ resource: 'users', action: 'view' }]}>
            <UserProfile />
          </RBACProtectedRoute>
        } />
        <Route path="analytics" element={
          <RBACProtectedRoute permissions={[{ resource: 'analytics', action: 'view' }]}>
            <Analytics />
          </RBACProtectedRoute>
        } />
        <Route path="performance" element={
          <RBACProtectedRoute permissions={[{ resource: 'system', action: 'configure' }]}>
            <PerformancePage />
          </RBACProtectedRoute>
        } />
        <Route path="retrospective" element={
          <RBACProtectedRoute permissions={[{ resource: 'analytics', action: 'view' }]}>
            <RetrospectiveAnalysis />
          </RBACProtectedRoute>
        } />
        <Route path="reporting/*" element={
          <RBACProtectedRoute permissions={[{ resource: 'analytics', action: 'view' }]}>
            <ReportingDashboard />
          </RBACProtectedRoute>
        } />
        <Route path="epic-reporting" element={
          <RBACProtectedRoute permissions={[{ resource: 'analytics', action: 'read' }]}>
            <EpicReporting />
          </RBACProtectedRoute>
        } />
        <Route path="sprint-reporting" element={
          <RBACProtectedRoute permissions={[{ resource: 'analytics', action: 'read' }]}>
            <SprintReporting />
          </RBACProtectedRoute>
        } />
        <Route path="settings" element={
          <RBACProtectedRoute permissions={[{ resource: 'users', action: 'view', scope: 'self' }]}>
            <Settings />
          </RBACProtectedRoute>
        } />
        <Route path="queue-monitor" element={
          <RBACProtectedRoute permissions={[{ resource: 'system', action: 'configure' }]}>
            <QueueMonitor />
          </RBACProtectedRoute>
        } />
        <Route path="employees" element={
          <RBACProtectedRoute permissions={[{ resource: 'employees', action: 'view', scope: 'global' }]}>
            <Employees />
          </RBACProtectedRoute>
        } />
        <Route path="employees/:id" element={
          <RBACProtectedRoute permissions={[{ resource: 'employees', action: 'view', scope: 'global' }]}>
            <EmployeeDetailPage />
          </RBACProtectedRoute>
        } />
        <Route path="users" element={
          <RBACProtectedRoute permissions={[{ resource: 'users', action: 'view', scope: 'global' }]}>
            <Users />
          </RBACProtectedRoute>
        } />
        <Route path="admin/system" element={
          <RBACProtectedRoute permissions={[{ resource: 'system', action: 'configure' }]}>
            <SystemAdmin />
          </RBACProtectedRoute>
        } />
        <Route path="admin/imports/*" element={
          <RBACProtectedRoute permissions={[{ resource: 'imports', action: 'view', scope: 'global' }]}>
            <ImportMonitor />
          </RBACProtectedRoute>
        } />
        <Route path="ssr-monitoring" element={
          <RBACProtectedRoute permissions={[{ resource: 'monitoring', action: 'view', scope: 'global' }]}>
            <SsrMonitoring />
          </RBACProtectedRoute>
        } />
        <Route path="incidents" element={
          <RBACProtectedRoute permissions={[{ resource: 'incidents', action: 'view', scope: 'global' }]}>
            <Incidents />
          </RBACProtectedRoute>
        } />
        <Route path="tools/styles-check" element={
          <RBACProtectedRoute permissions={[{ resource: 'tools', action: 'execute', scope: 'global' }]}>
            <StylesCheckPage />
          </RBACProtectedRoute>
        } />
        <Route path="incidents/:id" element={
          <RBACProtectedRoute permissions={[{ resource: 'incidents', action: 'view', scope: 'global' }]}>
            <IncidentDetail />
          </RBACProtectedRoute>
        } />
        <Route path="worktime" element={
          <RBACProtectedRoute permissions={[{ resource: 'worktime', action: 'view', scope: 'global' }]}>
            <Worktime />
          </RBACProtectedRoute>
        } />
        <Route path="worktime/settings" element={
          <RBACProtectedRoute permissions={[{ resource: 'worktime', action: 'manage', scope: 'global' }]}>
            <PresenceSettings />
          </RBACProtectedRoute>
        } />
        {/* Chat Surveys (docs/chat-surveys.md) — My surveys is for every authenticated user; the Manage tab gates itself. */}
        <Route path="surveys" element={
          <RBACProtectedRoute>
            <Surveys />
          </RBACProtectedRoute>
        } />
        <Route path="surveys/:id" element={
          <RBACProtectedRoute permissions={[{ resource: 'surveys', action: 'view', scope: 'global' }]}>
            <SurveyResults />
          </RBACProtectedRoute>
        } />
        <Route path="admin/breathehr" element={
          <RBACProtectedRoute permissions={[{ resource: 'imports', action: 'view', scope: 'global' }]}>
            <BreatheHRPage />
          </RBACProtectedRoute>
        } />
        <Route path="admin/epic-analysis" element={
          <RBACProtectedRoute permissions={[{ resource: 'system', action: 'configure' }]}>
            <EpicAnalysis />
          </RBACProtectedRoute>
        } />
        <Route path="admin/clickup-management" element={
          <RBACProtectedRoute permissions={[{ resource: 'integrations', action: 'view', scope: 'global' }]}>
            <ClickUpManagement />
          </RBACProtectedRoute>
        } />
        <Route path="clickup/tasks/:type/:id" element={
          <RBACProtectedRoute permissions={[{ resource: 'integrations', action: 'view' }]}>
            <ClickUpTasks />
          </RBACProtectedRoute>
        } />
        <Route path="admin/quote-management" element={
          <RBACProtectedRoute permissions={[{ resource: 'system', action: 'configure' }]}>
            <QuoteManagement />
          </RBACProtectedRoute>
        } />
        <Route path="admin/audit" element={
          <RBACProtectedRoute permissions={[{ resource: 'audit_logs', action: 'view' }]}>
            <AuditDashboard />
          </RBACProtectedRoute>
        } />
        <Route path="admin/tools" element={
          <RBACProtectedRoute permissions={[{ resource: 'system', action: 'configure' }]}>
            <AdminTools />
          </RBACProtectedRoute>
        } />
        <Route path="admin/retention-policies" element={
          <RBACProtectedRoute permissions={[{ resource: 'retention_policies', action: 'view' }]}>
            <RetentionPolicyDashboard />
          </RBACProtectedRoute>
        } />
        {/* Admin HR Data Pages */}
        <Route path="admin/working-patterns" element={
          <RBACProtectedRoute permissions={[{ resource: 'system', action: 'configure' }]}>
            <WorkingPatternsPage />
          </RBACProtectedRoute>
        } />
        <Route path="admin/departments" element={
          <RBACProtectedRoute permissions={[{ resource: 'system', action: 'configure' }]}>
            <DepartmentsPage />
          </RBACProtectedRoute>
        } />
        <Route path="admin/divisions" element={
          <RBACProtectedRoute permissions={[{ resource: 'system', action: 'configure' }]}>
            <DivisionsPage />
          </RBACProtectedRoute>
        } />
        <Route path="admin/locations" element={
          <RBACProtectedRoute permissions={[{ resource: 'system', action: 'configure' }]}>
            <LocationsPage />
          </RBACProtectedRoute>
        } />
        <Route path="admin/roles" element={
          <RBACProtectedRoute permissions={[{ resource: 'roles', action: 'manage', scope: 'global' }]}>
            <RoleManagementPage />
          </RBACProtectedRoute>
        } />
        <Route path="admin/roles/:roleId" element={
          <RBACProtectedRoute permissions={[{ resource: 'roles', action: 'manage', scope: 'global' }]}>
            <RoleManagementPage />
          </RBACProtectedRoute>
        } />
        <Route path="admin/permission-usage" element={
          <RBACProtectedRoute permissions={[{ resource: 'roles', action: 'manage', scope: 'global' }]}>
            <PermissionUsageReportPage />
          </RBACProtectedRoute>
        } />
        <Route path="admin/infrastructure" element={
          <RBACProtectedRoute permissions={[{ resource: 'infrastructure', action: 'view', scope: 'global' }]}>
            <InfrastructureDiscoveryPage />
          </RBACProtectedRoute>
        } />
        <Route path="2fa-codes" element={
          <RBACProtectedRoute permissions={[{ resource: 'projects', action: 'view' }]}>
            <TotpCodes />
          </RBACProtectedRoute>
        } />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
};

// Toast Container Component
const AppWithToasts: React.FC = () => {
  const { notifications, removeNotification } = useNotifications();

  return (
    <div className="App">
      <AppRoutes />
      <ToastContainer
        notifications={notifications}
        onClose={removeNotification}
        position="top-right"
        maxToasts={5}
      />
      {/* Debug FABs: dev server only (statically dropped from production builds). */}
      {import.meta.env.DEV && <LazyLoadingMonitor enabled />}
      {import.meta.env.DEV && <AssetLoadingMonitor enabled />}
    </div>
  );
};

function App() {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <AuthInitializer>
          <RBACProvider>
            <NotificationProvider>
              <Router>
                <AppWithToasts />
              </Router>
            </NotificationProvider>
          </RBACProvider>
        </AuthInitializer>
        {/* {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />} */}
      </QueryClientProvider>
    </Provider>
  );
}

export default App;
